import { useState } from 'react';
import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import { Field, Stack, Button, tokens, Select, Checkbox, Link, Text } from '@hotjar/hotsauce';
import {
  ConsentDetails,
  ConsentDetailsBlock,
  ConsentExpandLink,
  ConsentWithdrawLink,
  Form,
  StyledCheckbox,
  Title,
} from '@hotjar/webapp/features/Registration/common/styles';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useRoleChoices } from '@hotjar/webapp/hooks/useRoleChoices';
import { useCountryChoices } from '@hotjar/webapp/hooks/useCountryChoices';
import { trackUserRoleSubmitted } from '@hotjar/webapp/domains/activation/onboarding/tracking';
import { useUserExperiment } from '@hotjar/webapp/hooks/useExperiment';
import { useCurrentUser } from '@hotjar/user';
import type { OrganizationSizeValue } from '@hotjar/webapp/hooks/useOrganizationSizeChoices';
import { useOnboardingNavigation } from '../hooks/useOnboardingNavigation';
import { useGeoLocation } from '../../../hooks/useGeoLocation';
import { isInLocale } from '../utils/locales';
import { PersonalisedTestimonials } from './PersonalisedTestimonials/PersonalisedTestimonials';
import { getTestimonial } from './PersonalisedTestimonials/utils';
import { PersonalisedTestimonialsLayout as Layout } from './PersonalisedTestimonials/PersonalisedTestimonialsLayout';

export type Role =
  | 'data analyst'
  | 'consultant'
  | 'digital marketer'
  | 'product manager'
  | 'ceo'
  | 'product designer'
  | 'engineer'
  | 'content designer'
  | 'ux researcher'
  | 'other';

export interface OrgFinalDetails {
  role: Role;
  subscribeToNewsletter: boolean;
  consentToParentCompanyRules: boolean;
  country?: string;
}

type UserId = Parameters<ReturnType<typeof useOnboardingNavigation>['getOrgDetails']>[0];

export interface OrganizationRoleProps {
  onSubmit: (finalDetails: OrgFinalDetails) => void;
  isSubmitInProgress?: boolean;
  userId: UserId;
}

const FormWrapper = styled(Form)`
  max-width: 465px;
  margin: 0 auto;
`;

const StyledTitle = styled(Title)`
  margin-bottom: ${tokens.spacer.x2};
`;

export const StyledField = styled(Field)`
  width: 100%;
`;

const ConsentDetailsHeight = '244px';

const NextButton = styled(Button)`
  width: fit-content;
`;

export const OrganizationRole = ({
  onSubmit,
  isSubmitInProgress = false,
  userId,
}: OrganizationRoleProps): JSX.Element => {
  const { t } = useTranslation();
  const { getOrgDetails } = useOnboardingNavigation();
  const [organizationRole, setOrganizationRole] = useState<Role | string>(
    getOrgDetails(userId)?.role || '',
  );
  const [customCountry, setCustomCountry] = useState(getOrgDetails(userId)?.country || '');

  const [isSubscriptionChecked, setisSubscriptionChecked] = useState(false);
  const [hasAcceptedConsentToParentCompanyRules, sethasAcceptedConsentToParentCompanyRules] =
    useState(false);
  const [isExpandedConsent, setExpandedConsent] = useState(false);
  const roles = useRoleChoices();
  const { click: trackClick } = useTrack();
  const { data: country } = useGeoLocation();
  const countries = useCountryChoices();
  const { data: user } = useCurrentUser();
  const { inVariant1: inPersonalisedTestimonialsExperiment } = useUserExperiment(
    'av_personalised_testimonials',
    user,
    isInLocale('en'),
  );
  const organizationSize = getOrgDetails(userId)?.organizationSize || '1-10';
  const testimonial = getTestimonial({
    size: organizationSize as OrganizationSizeValue,
  });

  const onConsentExpand = () => {
    setExpandedConsent(!isExpandedConsent);
    trackClick('View Parent Company Consent Details', !isExpandedConsent ? 'Expand' : 'Close', {});

    // wait for consent details block to expand
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 0);
  };

  const handleOnSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrganizationRole(e.target.value as Role);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackUserRoleSubmitted({ role: organizationRole as Role });
    trackClick('Role details submitted', 'Next - User Role', {});
    onSubmit({
      role: organizationRole as Role,
      subscribeToNewsletter: isSubscriptionChecked,
      consentToParentCompanyRules: hasAcceptedConsentToParentCompanyRules,
      country: country || customCountry,
    });
  };

  return (
    <Layout twoColumn={inPersonalisedTestimonialsExperiment} dataTestid="organization-steps-role">
      <FormWrapper onSubmit={handleSubmit} noValidate>
        <StyledTitle aria-live="assertive">
          {t('activation:onboarding.organization-steps.role.title')}
        </StyledTitle>
        <Stack preset="form">
          <Select
            size="l"
            id="role"
            name="role"
            isRequired
            data-testid="organization-role"
            defaultValue={organizationRole || getOrgDetails(userId)?.role || ''}
            autoFocus
            onChange={handleOnSelectChange}
          >
            <option value="" disabled>
              {t('activation:onboarding.organization-steps.select-placeholder')}
            </option>
            {roles.map(({ value, label }) => (
              <option value={value} key={value}>
                {t(label)}
              </option>
            ))}
          </Select>
          {!country ? (
            <StyledField
              inputId="country"
              isRequired
              labelText={t('activation:onboarding.organization-details.country.label')}
            >
              <Select
                size="l"
                id="country"
                name="country"
                isRequired
                data-testid="country"
                onChange={(e) => setCustomCountry(e.target.value)}
                defaultValue={customCountry}
              >
                <option value="" disabled>
                  {t('activation:onboarding.organization-details.select-placeholder')}
                </option>
                {countries.map(({ value, label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </StyledField>
          ) : null}
          <Checkbox
            id="subscribeToNewsletter"
            name="subscribeToNewsletter"
            value="true"
            isChecked={isSubscriptionChecked}
            onChange={() => setisSubscriptionChecked(!isSubscriptionChecked)}
            labelContent={t('activation:onboarding.organization-details.subscribe-to-newsletter')}
          />
          <div>
            <StyledCheckbox
              id="consentToParentCompanyRules"
              name="consentToParentCompanyRules"
              value="true"
              isChecked={hasAcceptedConsentToParentCompanyRules}
              onChange={() =>
                sethasAcceptedConsentToParentCompanyRules(!hasAcceptedConsentToParentCompanyRules)
              }
              labelContent={
                <div id="consentToParentCompanyRulesLabel">
                  {t('common:registration.select-signup-method.content-square-consent')}
                  <ConsentExpandLink
                    as="button"
                    kind="inline"
                    type="button"
                    onClick={onConsentExpand}
                  >
                    {isExpandedConsent
                      ? t('common:registration.select-signup-method.consent-read-less')
                      : t('common:registration.select-signup-method.consent-read-more')}
                  </ConsentExpandLink>
                </div>
              }
            />
            <ConsentDetails
              css={{
                maxHeight: isExpandedConsent ? ConsentDetailsHeight : 0,
                visibility: isExpandedConsent ? 'visible' : 'hidden', // This is done to prevent browser from focusing on the links in the block, when it's not expanded
              }}
            >
              <ConsentDetailsBlock>
                <Trans i18nKey="common:registration.select-signup-method.consent-details-1">
                  <Link
                    as="a"
                    kind="inline"
                    isExternal
                    href="https://www.hotjar.com/blog/hotjar-and-contentsquare/"
                  >
                    which Hotjar forms part of
                  </Link>
                </Trans>
              </ConsentDetailsBlock>
              <ConsentDetailsBlock>
                <Trans i18nKey="common:registration.select-signup-method.consent-details-2">
                  <Link kind="inline" isExternal href="https://contentsquare.com/hotjar/">
                    The Contentsquare Group
                  </Link>
                </Trans>
              </ConsentDetailsBlock>
              <ConsentDetailsBlock>
                <Text textType="subtitle3">
                  <Trans i18nKey="common:registration.select-signup-method.consent-details-3">
                    <ConsentWithdrawLink kind="inline" href="mailto:dpo@hotjar.com">
                      dpo@hotjar.com
                    </ConsentWithdrawLink>
                  </Trans>
                </Text>
              </ConsentDetailsBlock>
            </ConsentDetails>
          </div>
          <NextButton
            size="l"
            type="submit"
            id="submit-step-role"
            data-testid="submit-step-role"
            isLoading={isSubmitInProgress}
            isDisabled={organizationRole.length < 1 || (!country && !customCountry)}
            onClick={handleSubmit}
            loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
          >
            {t('activation:onboarding.organization-steps.submit-cta')}
          </NextButton>
        </Stack>
      </FormWrapper>
      {inPersonalisedTestimonialsExperiment ? (
        <PersonalisedTestimonials background="round" testimonial={testimonial} />
      ) : null}
    </Layout>
  );
};
